import { Commit, Dispatch } from "vuex";
import ApiService from "@/services/api.service";
import User from "@/models/User";
import type {
    SubscriptionPlan,
    SubscriptionSeason,
} from "@/types/Subscription";

export interface ProfileUser {
    id?: number;
    name?: string;
    email?: string;
    locale?: string;
    profilePicture?: string;
    receiveSoccerReminderMails?: boolean;
    isAdmin?: boolean;
    permissions?: string[];
    subscriptionTypeRecurring?: SubscriptionPlan | null;
    subscriptionSeasons?: SubscriptionSeason[];
    [key: string]: any;
}

interface ProfileState {
    profile: {
        user: ProfileUser;
        group: any;
    };
}

const state: ProfileState = {
    profile: {
        user: {},
        group: {},
    },
};

const getters = {
    user: (state: ProfileState) => state.profile.user,
    group: (state: ProfileState) => state.profile.group,
    profile: (state: ProfileState) => state.profile,
};

const mutations = {
    setUser(state: ProfileState, user: ProfileUser) {
        state.profile.user = user ? (new User(user) as ProfileUser) : user;
    },
    clear(state: ProfileState) {
        state.profile.user = {};
        state.profile.group = {};
    },
};

const actions = {
    async fetchUser({ commit }: { commit: Commit }) {
        try {
            const response = await ApiService.get("/profile");
            return commit("setUser", response.data.data);
        } catch (error: any) {
            // Handle 401 gracefully - user is not authenticated
            if (error.response?.status === 401) {
                commit("clear");
                return;
            }
            throw error;
        }
    },
    async update(
        context: any,
        data: {
            name: string;
            email: string;
            receiveSoccerReminderMails: boolean;
        },
    ) {
        const response = await ApiService.post("/profile/edit", data);
        // Don't update the store here - let fetchUser handle it
        // This ensures we always have the correct user data structure
        return response;
    },
    updatePassword(
        context: any,
        data: { password: string; new_password: string },
    ) {
        return ApiService.post("/profile/password/edit", data);
    },
    async clear({ dispatch }: { dispatch: Dispatch }) {
        await dispatch("clear");
    },
    async delete({ commit }: { commit: Commit }) {
        await commit("clear");
        return ApiService.delete("/profile").then(() => {
            commit("clear");
        });
    },
};

export const profile = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
